import type {
  BinningFilterStrategy,
  BinningOrderByOptions,
  BinningStrategy,
  KeyGetter
} from "./binning";
import type { BinnableLeg } from "./binning-strategies";
import type { FilterStrategyBuilderActions } from "./filter-builder";

import { binResults, buildBinningBase } from "./binning";
import { filterStrategyBuilder } from "./filter-builder";

type BinStrategyBuilderStrategies<T, R = T[], F = R[]> = {
  bin: BinningStrategy<T>;
  filter: BinningFilterStrategy<T, F> | undefined;
  filter_builder: (
    builder: FilterStrategyBuilderActions<T, R, F>
  ) => FilterStrategyBuilderActions<T, R, F>;
  order: BinningOrderByOptions<T> | undefined;
  format: ((results: F[]) => F) | undefined;
};

export function BinStrategyBuilder<
  T = BinnableLeg, // Object to be binned
  BinValue = T, // Result from the binning strategy
  Transformed = BinValue[][] // Final transformed & formatted output
>() {
  const strategies = {} as BinStrategyBuilderStrategies<
    T,
    BinValue,
    Transformed
  >;

  const builder = {
    binBy(bin_strategy: KeyGetter<T>) {
      strategies.bin = buildBinningBase(bin_strategy).bin;
      return builder;
    },
    filterBy(filter_strategy: BinningFilterStrategy<T, Transformed>) {
      strategies.filter = filter_strategy;
      return builder;
    },
    createFilter(
      filter_builder: (
        builder: FilterStrategyBuilderActions<T, BinValue, Transformed>
      ) => FilterStrategyBuilderActions<T, BinValue, Transformed>
    ) {
      strategies.filter_builder = filter_builder;
      return builder;
    },
    orderBy(options: BinningOrderByOptions<T>) {
      strategies.order = options;
      return builder;
    },
    format: {
      results(format: (results: Transformed[]) => Transformed) {
        strategies.format = format;
        return builder;
      }
    },
    build() {
      const bin_strategy = strategies.bin;
      if (!bin_strategy) {
        throw new Error("BinStrategyBuilder: binning strategy is required");
      }

      return (values: T[]) => {
        const { filter, filter_builder, format } = strategies;

        let filterStrategy: BinningFilterStrategy<T, Transformed> | undefined;

        if (filter_builder) {
          filterStrategy = (values, bin_key, bin) => {
            return filter_builder(
              filterStrategyBuilder<T, BinValue, Transformed>()
            ).build()(values, bin_key, bin);
          };
        } else if (filter) {
          filterStrategy = (values, bin_key, bin) =>
            filter(values, bin_key, bin);
        }

        const results = binResults<T, Transformed>(values, {
          binningStrategy: bin_strategy,
          filterStrategy,
          orderByOptions: strategies.order
        });

        if (format) {
          return format(results) as Transformed;
        }

        return results as Transformed;
      };
    }
  } as const;

  return builder;
}
